import React from 'react'
import { SavedAudience } from '@marketing-milk/interfaces'
import { audienceTypes } from '@marketing-milk/frontend'
import { radius, SurfacePalette, type } from '../../tokens'
import { audienceSizeText, audienceSummary, audienceTags } from './audienceTargeting'

interface Props {
  palette: SurfacePalette
  audiences: SavedAudience[]
  primaryAudienceID: number | null
  /** Edit-only: render the "Edit selection" control and empty-state CTA. */
  editable?: boolean
  onEditSelection?: () => void
  /** Open the full-targeting deep-dive for an audience. */
  onOpenDetail: (audience: SavedAudience) => void
}

const Muted = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>{children}</p>
)

const Chip = ({
  palette,
  children,
  wrap,
}: {
  palette: SurfacePalette
  children: React.ReactNode
  /** Allow long text (e.g. a full address) to wrap and stay inside the card. */
  wrap?: boolean
}) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: radius.sm,
      border: `1px solid ${palette.hairline}`,
      background: palette.bg,
      color: palette.textMuted,
      fontFamily: type.family.sans,
      fontSize: type.size.eyebrow,
      fontWeight: type.weight.medium,
      ...(wrap
        ? { whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' }
        : { whiteSpace: 'nowrap' }),
    }}
  >
    {children}
  </span>
)

const Eyebrow = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <div
    style={{
      fontFamily: type.family.mono,
      fontSize: type.size.eyebrow,
      fontWeight: type.weight.medium,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: palette.accent,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
)

const TagRow = ({
  palette,
  tags,
  max,
}: {
  palette: SurfacePalette
  tags: string[]
  max: number
}) => {
  if (tags.length === 0) return null
  const shown = tags.slice(0, max)
  const extra = tags.length - shown.length
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
      {shown.map(t => (
        <Chip key={t} palette={palette}>
          {t}
        </Chip>
      ))}
      {extra > 0 && <Chip palette={palette}>+{extra} more</Chip>}
    </div>
  )
}

const typeTitle = (a: SavedAudience): string => audienceTypes[a.type]?.title ?? a.type

// Clickable card chrome shared by the primary detail and secondary cards:
// button semantics (Enter/Space) plus a keyboard-visible accent ring — inline
// styles can't express :focus-visible, so focus is tracked in state.
const DetailCard = ({
  palette,
  padding,
  onOpen,
  style,
  children,
}: {
  palette: SurfacePalette
  padding: number
  onOpen: () => void
  style?: React.CSSProperties
  children: React.ReactNode
}) => {
  const [focused, setFocused] = React.useState(false)
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        cursor: 'pointer',
        borderRadius: radius.md,
        border: `1px solid ${focused ? palette.accent : palette.hairline}`,
        boxShadow: focused ? `0 0 0 2px ${palette.accent}` : undefined,
        outline: 'none',
        background: palette.surface,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

const PrimaryDetail = ({
  palette,
  audience,
  onOpenDetail,
}: {
  palette: SurfacePalette
  audience: SavedAudience
  onOpenDetail: (a: SavedAudience) => void
}) => {
  const summary = audienceSummary(audience)
  return (
    <DetailCard palette={palette} padding={24} onOpen={() => onOpenDetail(audience)}>
      <Eyebrow palette={palette}>Primary audience</Eyebrow>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontFamily: type.family.sans,
            fontSize: type.size.h3,
            fontWeight: type.weight.semibold,
            color: palette.text,
          }}
          title={audience.name}
        >
          {audience.name}
        </h3>
        <span
          style={{
            fontSize: type.size.bodyLg,
            color: palette.text,
            fontWeight: type.weight.medium,
          }}
        >
          {audienceSizeText(audience)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Chip palette={palette}>{typeTitle(audience)}</Chip>
        <Chip palette={palette}>{summary.age}</Chip>
        <Chip palette={palette}>{summary.genders}</Chip>
        <Chip palette={palette} wrap>
          {summary.location}
        </Chip>
      </div>
      <TagRow palette={palette} tags={audienceTags(audience.targeting)} max={8} />
      <div
        style={{
          marginTop: 16,
          color: palette.accent,
          fontSize: type.size.body,
          fontWeight: type.weight.medium,
        }}
      >
        View full targeting →
      </div>
    </DetailCard>
  )
}

const SecondaryCard = ({
  palette,
  audience,
  onOpenDetail,
}: {
  palette: SurfacePalette
  audience: SavedAudience
  onOpenDetail: (a: SavedAudience) => void
}) => {
  const summary = audienceSummary(audience)
  return (
    <DetailCard
      palette={palette}
      padding={18}
      onOpen={() => onOpenDetail(audience)}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            fontWeight: type.weight.semibold,
            color: palette.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={audience.name}
        >
          {audience.name}
        </span>
        <span style={{ fontSize: type.size.body, color: palette.textMuted, whiteSpace: 'nowrap' }}>
          {audienceSizeText(audience)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        <Chip palette={palette}>{typeTitle(audience)}</Chip>
        <Chip palette={palette} wrap>
          {summary.location}
        </Chip>
      </div>
      <TagRow palette={palette} tags={audienceTags(audience.targeting)} max={4} />
    </DetailCard>
  )
}

/**
 * Shared Audience renderer used by the Edit (`AudienceSection`) and Present
 * (`PresentAudienceSection`) hosts. Renders the AE-designated primary audience
 * as a detail block and the rest as secondary cards. Data is the packet's
 * curated selection (resolved server-side); this component is stateless and
 * takes callbacks for editing the selection (Edit only) and opening the
 * full-targeting deep-dive (both modes).
 */
export const AudienceContent = ({
  palette,
  audiences,
  primaryAudienceID,
  editable,
  onEditSelection,
  onOpenDetail,
}: Props) => {
  if (audiences.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
        <Muted palette={palette}>No audiences selected for this packet yet.</Muted>
        {editable && onEditSelection && (
          <EditSelectionButton
            palette={palette}
            onClick={onEditSelection}
            label="Select audiences"
          />
        )}
      </div>
    )
  }

  const primary = audiences.find(a => a.id === primaryAudienceID) ?? audiences[0]
  const secondary = audiences.filter(a => a.id !== primary.id)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {editable && onEditSelection && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <EditSelectionButton palette={palette} onClick={onEditSelection} label="Edit selection" />
        </div>
      )}
      <PrimaryDetail palette={palette} audience={primary} onOpenDetail={onOpenDetail} />
      {secondary.length > 0 && (
        <div>
          <Eyebrow palette={palette}>Secondary audiences</Eyebrow>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}
          >
            {secondary.map(a => (
              <SecondaryCard
                key={a.id}
                palette={palette}
                audience={a}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const EditSelectionButton = ({
  palette,
  onClick,
  label,
}: {
  palette: SurfacePalette
  onClick: () => void
  label: string
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${palette.accent}`,
      background: 'transparent',
      color: palette.accent,
      borderRadius: radius.sm,
      padding: '6px 14px',
      fontFamily: type.family.sans,
      fontSize: type.size.body,
      fontWeight: type.weight.medium,
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
)
